// @flow
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { selectSponsoredProductsCategory } from 'selectors/categoriesSelectors';

import { ALL_CATEGORY, STATIC_CATEGORIES } from 'utils/categories';
import { track } from 'utils/segment';

import { ReactComponent as LogoSVG } from 'images/fff-logo.svg';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';
import BurgerMenu from '../BurgerMenu/BurgerMenu';
import Timer from '../Timer/Timer';
import Categories from '../Categories/Categories';
import SearchBar from '../SearchBar/SearchBar';
import ErrorBoundary from '../../../shared-components/ErrorBoundary/ErrorBoundary';
import { receiveUpdateCart } from 'services/actions/cartActions';

import createMenuItems from '../menuItems';

import withWindowTopIntersection from '../../../HOC/withWindowTopIntersection/withWindowTopIntersection';
import withSelectedCategoryId from '../../../HOC/withSelectedCategoryId/withSelectedCategoryId';

import styles from './SecondaryHeader.module.css';

import type { Category, State } from '../../../sharedTypes';
import type { WithWindowTopIntersectionInjectedProps } from '../../../HOC/withWindowTopIntersection/withWindowTopIntersection';

type InjectedProps = {|
  ...WithWindowTopIntersectionInjectedProps,
  +selectedCategoryId: string | null,
|};

type OwnProps = {|
  +campaignEndDate: string,
  +viewSize: string,
  +version: string,
|};

type StateProps = {|
  +categories: Array<Category>,
  +loggedIn: boolean,
  +showSearchBar: boolean,
  +sponsoredProductsCategory: Array<any>,
  +trackingData: any,
|};

type Props = {| ...InjectedProps, ...OwnProps, ...StateProps |};

function SecondaryHeader(props: Props) {
  const {
    campaignEndDate,
    viewSize,
    version,
    windowTopIntersection,
    forwardedRef,
    categories,
    selectedCategoryId,
    loggedIn,
    showSearchBar,
    sponsoredProductsCategory,
    trackingData,
  } = props;

  const displayedCategories = [
    ...(loggedIn ? STATIC_CATEGORIES : ALL_CATEGORY),
    ...sponsoredProductsCategory,
    ...categories,
  ];
  const stuck = windowTopIntersection;
  // menu item data for medium and small view sizes
  const menuItems = createMenuItems(true, version, categories);

  // TODO: define this function in Categories and BurgerMenu components,
  // instead of PrimaryHeader and SecondaryHeader components.
  function handleCategoryTracking(categoryName: string) {
    const data = {
      ...trackingData,
      experience: 'Flash Sale Filter',
      filter_name: categoryName,
    };
    track('Products Filtered', data);
  }

  return (
    <header
      className={`${styles.header} ${styles[viewSize]}`}
      ref={forwardedRef}
    >
      <section
        className={`${styles.headerContent} page-row ${
          stuck ? styles.stuck : ''
        }`}
      >
        {stuck && (
          <div className={styles.leftPanel}>
            {viewSize === 'medium' && (
              <BurgerMenu
                menuItems={menuItems}
                handleCategoryTracking={handleCategoryTracking}
              />
            )}
            <a href="/" className={styles.logoContainer}>
              <span className="sr-only">visit our home page</span>
              <LogoSVG className={styles.logo} alt="FabFitFun Logo" />
            </a>
          </div>
        )}
        {(viewSize === 'xLarge' || viewSize === 'large') && (
          <ErrorBoundary>
            <SearchBar viewSize={viewSize} />
            {!showSearchBar && (
              <Categories
                categories={displayedCategories}
                selectedCategoryId={selectedCategoryId}
                handleCategoryTracking={handleCategoryTracking}
              />
            )}
          </ErrorBoundary>
        )}
        {viewSize === 'medium' && (
          <ErrorBoundary>
            <Timer
              endTime={campaignEndDate}
              version={version}
              viewSize={viewSize}
            />
          </ErrorBoundary>
        )}
      </section>
    </header>
  );
}

function mapStateToProps(
  state: State,
  props: {| ...InjectedProps, ...OwnProps |}
): StateProps {
  return {
    categories: state.categoriesState.categories,
    loggedIn: state.userState.loggedIn,
    showSearchBar: state.searchState.showSearchBar,
    sponsoredProductsCategory: selectSponsoredProductsCategory(state),
    trackingData: selectPageTrackingFullData(state),
  };
}

const mapDispatchToProps = {
  receiveUpdateCart,
};

export default compose(
  withWindowTopIntersection,
  withSelectedCategoryId,
  withLDConsumer(),
  connect(mapStateToProps, mapDispatchToProps)
)(SecondaryHeader);
